import { useState, useMemo, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Search, X, Star, Filter, MoreHorizontal, ChevronDown, ChevronRight, Trash2, Pencil, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFilters, useToggleFilterFavorite, useDeleteFilter, useUpdateFilter } from '../../hooks/useIssues';
import { useAuthStore } from '../../store/authStore';
import FavoriteToggle from '../ui/FavoriteToggle';

// ─── Collapsible Section ───
function CollapsibleSection({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 w-full text-sm font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors">
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {title}
      </button>
      {open && <div className="mt-1 space-y-0.5">{children}</div>}
    </div>
  );
}

// ─── Empty State ───
function EmptyState({ text }: { text: string }) {
  return <p className="px-3 py-2 text-sm text-gray-400">{text}</p>;
}

// ─── Filter Item ───
function FilterItem({ filter }: { filter: { id: string; name: string; userId: string; scope: string; isFavorite: boolean } }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const currentUser = useAuthStore((s) => s.user);
  const isOwner = filter.userId === currentUser?.id;
  const toggleFavorite = useToggleFilterFavorite();
  const deleteFilter = useDeleteFilter();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu) return;
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showMenu]);

  return (
    <div className="group relative">
      <NavLink
        to={`/filters/${filter.id}`}
        className={({ isActive }) =>
          `flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
            isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
          }`
        }
      >
        {filter.isFavorite
          ? <Star className="size-3.5 shrink-0 fill-yellow-400 text-yellow-400" />
          : <Filter className="size-3.5 shrink-0 text-gray-400" />}
        <span className="truncate flex-1">{filter.name}</span>
        <FavoriteToggle targetType="FILTER" targetId={filter.id} />
      </NavLink>

      {/* More menu trigger (hover) */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(!showMenu); }}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition-opacity"
      >
        <MoreHorizontal className="size-3.5 text-gray-400" />
      </button>

      {/* Dropdown menu */}
      {showMenu && (
        <div ref={menuRef} className="absolute right-0 top-full mt-1 w-40 bg-white border rounded-lg shadow-lg z-30 py-1">
          <button onClick={() => { toggleFavorite.mutate(filter.id); setShowMenu(false); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left">
            <Star className="size-3.5" />
            {filter.isFavorite ? '즐겨찾기 해제' : '즐겨찾기 등록'}
          </button>
          {isOwner && (
            <>
              <button onClick={() => { setShowEdit(true); setShowMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 text-left">
                <Pencil className="size-3.5" />
                수정
              </button>
              <button onClick={() => {
                if (confirm('이 필터를 삭제하시겠습니까?')) deleteFilter.mutate(filter.id);
                setShowMenu(false);
              }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 text-left">
                <Trash2 className="size-3.5" />
                삭제
              </button>
            </>
          )}
        </div>
      )}

      {/* Edit dialog */}
      {showEdit && (
        <FilterEditDialog filterId={filter.id} currentName={filter.name} currentScope={filter.scope as 'PERSONAL' | 'SHARED'}
          onClose={() => setShowEdit(false)} />
      )}
    </div>
  );
}

// ─── Filter Edit Dialog ───
function FilterEditDialog({ filterId, currentName, currentScope, onClose }: {
  filterId: string; currentName: string; currentScope: 'PERSONAL' | 'SHARED'; onClose: () => void;
}) {
  const [name, setName] = useState(currentName);
  const [scope, setScope] = useState<'PERSONAL' | 'SHARED'>(currentScope);
  const [saving, setSaving] = useState(false);
  const updateFilter = useUpdateFilter();

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateFilter.mutateAsync({ filterId, data: { name: name.trim(), scope } });
      toast.success('필터가 수정되었습니다.');
      onClose();
    } catch {
      toast.error('필터 수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-80 p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 mb-4">필터 수정</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) handleSave(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">범위</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="radio" name="editScope" value="PERSONAL" checked={scope === 'PERSONAL'}
                  onChange={() => setScope('PERSONAL')}
                  className="text-primary-600 focus:ring-primary-500" />
                개인
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="radio" name="editScope" value="SHARED" checked={scope === 'SHARED'}
                  onChange={() => setScope('SHARED')}
                  className="text-primary-600 focus:ring-primary-500" />
                공유
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">취소</button>
          <button onClick={handleSave} disabled={!name.trim() || saving}
            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FilterSubSidebar ───
export default function FilterSubSidebar() {
  const { data: filters } = useFilters();
  const currentUser = useAuthStore((s) => s.user);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredList = useMemo(() => {
    if (!filters) return [];
    if (!searchQuery.trim()) return filters;
    const q = searchQuery.trim().toLowerCase();
    return filters.filter((f) => f.name.toLowerCase().includes(q));
  }, [filters, searchQuery]);

  const favorites = filteredList.filter((f) => f.isFavorite);
  const myFilters = filteredList.filter((f) => f.scope === 'PERSONAL' && !f.isFavorite && f.userId === currentUser?.id);
  const sharedFilters = filteredList.filter((f) => f.scope === 'SHARED' && !f.isFavorite);

  return (
    <aside className="w-[280px] min-w-[280px] bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-gray-200 shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
            placeholder="필터 검색..."
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filter sections */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
        <CollapsibleSection title="즐겨찾기">
          {favorites.map((f) => <FilterItem key={f.id} filter={f} />)}
          {favorites.length === 0 && <EmptyState text="즐겨찾기한 필터가 없습니다." />}
        </CollapsibleSection>

        <CollapsibleSection title="내 필터">
          {myFilters.map((f) => <FilterItem key={f.id} filter={f} />)}
          {myFilters.length === 0 && <EmptyState text="저장된 필터가 없습니다." />}
        </CollapsibleSection>

        <CollapsibleSection title="공유 필터">
          {sharedFilters.map((f) => <FilterItem key={f.id} filter={f} />)}
          {sharedFilters.length === 0 && <EmptyState text="공유된 필터가 없습니다." />}
        </CollapsibleSection>
      </div>

      {/* 새 필터 — sticky footer. LNB 하단(Sidebar.tsx)의 border-t/h-14와 동일하게 맞춤 */}
      <div className="border-t border-gray-200 px-3 h-14 flex items-center shrink-0">
        <NavLink
          to="/filters"
          end
          className={({ isActive }) =>
            `flex items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors w-full ${
              isActive
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`
          }
        >
          <Plus className="size-3.5 shrink-0" />
          새 필터
        </NavLink>
      </div>

    </aside>
  );
}
